/**
 * SortingListings.js
 *
 * Shows geo-features where:
 *   - showable_in_filter_pane === true
 *   - filter_usage === 'sorting'
 *
 * Ordered by `priority` (ascending). The user can:
 *   - Toggle features on/off (click to select)
 *   - Drag them up/down to set personal priority order
 *
 * Scoring formula for a listing:
 *   score = Σ ( reversedRank(feature) * featureScore(listing, feature) )
 *
 * Where:
 *   reversedRank = (totalSelected - rank + 1)  → rank 1 = highest weight
 *   featureScore = listing's geo_feature value for that feature id
 *                  normalised to [0, 1] using the feature's min/max range
 *
 * Exports:
 *   - default: SortingListings (React component for FilterPanel accordion)
 *   - computeListingScore(listing, sortPriorities): number
 *   - scoreListings(listings, sortPriorities): listings sorted desc by score
 *   - getScoreColor(score): hex color string (red→green gradient)
 */

import { useState, useCallback } from 'react';

// ── Scoring utilities ────────────────────────────────────────────────────────

/**
 * Normalise a raw feature value to [0, 1] using the feature definition's range.
 * Falls back to clamped ratio if min===max.
 */
function normaliseValue(rawValue, featureDef) {
    const min = featureDef.min_value ?? 0;
    const max = featureDef.max_value ?? 100;
    if (max === min) return rawValue >= max ? 1 : 0;
    return Math.min(1, Math.max(0, (rawValue - min) / (max - min)));
}

/**
 * Compute a final sort score for one listing.
 *
 * @param {object}   listing        - raw listing object from API
 * @param {Array}    sortPriorities - [{ featureId, rank, featureDef }, ...]
 *                                    rank 1 = highest importance
 * @returns {number} score ≥ 0
 */
export function computeListingScore(listing, sortPriorities) {
    if (!sortPriorities || sortPriorities.length === 0) return 0;

    const n = sortPriorities.length;
    const propGeoFeatures = listing.property?.geo_features || [];

    return sortPriorities.reduce((total, { featureId, rank, featureDef }) => {
        const gf = propGeoFeatures.find(f => f.id === featureId);
        if (!gf || gf.value == null) return total;

        const reversedRank = n - rank + 1;   // rank 1 → weight n, rank n → weight 1
        const normScore = normaliseValue(parseFloat(gf.value), featureDef);
        return total + reversedRank * normScore;
    }, 0);
}

/**
 * Sort a list of listings by score descending.
 * Listings with no matching geo-features score 0 and sink to the bottom.
 */
export function scoreListings(listings, sortPriorities) {
    if (!sortPriorities || sortPriorities.length === 0) return listings;
    return [...listings].sort(
        (a, b) => computeListingScore(b, sortPriorities) - computeListingScore(a, sortPriorities)
    );
}

/**
 * Map a normalised score in [0, 1] to an RGB hex color.
 * 0 → red (#ef4444), 0.5 → amber (#f59e0b), 1 → green (#22c55e)
 */
export function getScoreColor(normScore) {
    const t = Math.min(1, Math.max(0, normScore));
    if (t <= 0.5) {
        // red → amber
        const r = 239;
        const g = Math.round(68 + (158 - 68) * (t / 0.5));
        const b = Math.round(68 + (11 - 68) * (t / 0.5));
        return `rgb(${r},${g},${b})`;
    } else {
        // amber → green
        const r = Math.round(245 + (34 - 245) * ((t - 0.5) / 0.5));
        const g = Math.round(158 + (197 - 158) * ((t - 0.5) / 0.5));
        const b = Math.round(11 + (94 - 11) * ((t - 0.5) / 0.5));
        return `rgb(${r},${g},${b})`;
    }
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * SortingListings
 *
 * Props:
 *   geoFeatures     {Array}    - all geo-feature definitions from API
 *   sortPriorities  {Array}    - [{ featureId, rank, featureDef }]
 *   onChange        {Function} - called with updated sortPriorities array
 */
export default function SortingListings({ geoFeatures = [], sortPriorities = [], onChange }) {
    // Only features flagged for the filter pane + sorting usage
    const sortableFeatures = [...geoFeatures]
        .filter(f => f.is_active !== false
            && f.filter_usage === 'sorting'
            && f.showable_in_filter_pane === true)
        .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

    const selectedIds = new Set(sortPriorities.map(p => p.featureId));

    // ── Toggle a feature on/off ───────────────────────────────────────────
    const toggleFeature = useCallback((feature) => {
        if (selectedIds.has(feature.id)) {
            // remove
            const next = sortPriorities
                .filter(p => p.featureId !== feature.id)
                .map((p, i) => ({ ...p, rank: i + 1 }));
            onChange(next);
        } else {
            // add at end
            const next = [
                ...sortPriorities,
                { featureId: feature.id, rank: sortPriorities.length + 1, featureDef: feature },
            ];
            onChange(next);
        }
    }, [sortPriorities, selectedIds, onChange]);

    // ── Move item up or down in the ranked list ──────────────────────────
    const moveItem = useCallback((idx, dir) => {
        const next = [...sortPriorities];
        const target = idx + dir;
        if (target < 0 || target >= next.length) return;
        [next[idx], next[target]] = [next[target], next[idx]];
        onChange(next.map((p, i) => ({ ...p, rank: i + 1 })));
    }, [sortPriorities, onChange]);

    if (sortableFeatures.length === 0) {
        return (
            <p style={{ fontFamily: 'Vazirmatn, sans-serif', fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center', padding: '8px 0' }}>
                ویژگی جغرافیایی قابل مرتب‌سازی تعریف نشده است.
            </p>
        );
    }

    return (
        <div className="sl-root" dir="rtl">
            {/* ── Step 1: selectable feature chips ── */}
            <div className="sl-hint">
                ویژگی‌های مورد نظر را انتخاب کنید
            </div>
            <div className="sl-chip-grid">
                {sortableFeatures.map(f => {
                    const on = selectedIds.has(f.id);
                    const rank = on ? sortPriorities.find(p => p.featureId === f.id)?.rank : null;
                    return (
                        <button
                            key={f.id}
                            type="button"
                            className={`sl-chip ${on ? 'sl-chip--on' : ''}`}
                            onClick={() => toggleFeature(f)}
                            aria-pressed={on}
                        >
                            {on && rank != null && (
                                <span className="sl-chip-rank">{rank}</span>
                            )}
                            {on && <span className="sl-chip-check">✓ </span>}
                            {f.description || f.name}
                        </button>
                    );
                })}
            </div>

            {/* ── Step 2: ranked drag list ── */}
            {sortPriorities.length > 0 && (
                <>
                    <div className="sl-hint sl-hint--rank">
                        ↕ ترتیب اولویت را تنظیم کنید — اولین مورد بیشترین وزن دارد
                    </div>
                    <ul className="sl-rank-list">
                        {sortPriorities.map((item, idx) => {
                            const def = item.featureDef
                                || sortableFeatures.find(f => f.id === item.featureId);
                            const label = def?.description || def?.name || `ویژگی ${item.featureId}`;
                            return (
                                <li key={item.featureId} className="sl-rank-item">
                                    <span className="sl-rank-num">{item.rank}</span>

                                    <div className="sl-move-btns">
                                        <button
                                            type="button"
                                            className="sl-move-btn"
                                            onClick={() => moveItem(idx, -1)}
                                            disabled={idx === 0}
                                            aria-label="اولویت بالاتر"
                                        >▲</button>
                                        <button
                                            type="button"
                                            className="sl-move-btn"
                                            onClick={() => moveItem(idx, 1)}
                                            disabled={idx === sortPriorities.length - 1}
                                            aria-label="اولویت پایین‌تر"
                                        >▼</button>
                                    </div>

                                    <span className="sl-rank-label">{label}</span>

                                    <button
                                        type="button"
                                        className="sl-remove-btn"
                                        onClick={() => toggleFeature(def || { id: item.featureId })}
                                        aria-label="حذف"
                                    >✕</button>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </div>
    );
}
